import learningMaterial from 'ApiService/Teacher/learningMaterial';
import Card from 'components/card';
import { formatDate } from 'pages/helpers/dateUtils';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const Index = () => {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUrl, setEditingUrl] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    url: '',
    description: '',
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const fetchUrls = async () => {
    setLoading(true);
    try {
      const response = await learningMaterial.getTrainingMaterialUrls();
      if (response.data.success) {
        setUrls(response.data.data);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch URLs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUrls();
  }, []);

  const handleEditClick = (urlItem) => {
    setEditingUrl(urlItem);
    setEditForm({
      title: urlItem.title,
      url: urlItem.url,
      description: urlItem.description || '',
    });
    setIsEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateSubmit = async () => {
    if (!editForm.title || !editForm.url) {
      toast.error('Title and URL are required.');
      return;
    }

    try {
      const response = await learningMaterial.updateTrainingMaterialUrl(
        editingUrl._id,
        editForm
      );
      if (response.data.success) {
        toast.success('URL updated successfully.');
        setIsEditModalOpen(false);
        fetchUrls();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update URL.');
    }
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await learningMaterial.deleteTrainingMaterialUrl(
        deletingId
      );
      if (response.data.success) {
        toast.success('URL deleted successfully.');
        setIsDeleteModalOpen(false);
        fetchUrls();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete URL.');
    }
  };

  return (
    <div className="mt-3 h-full w-full">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
          Training Material URLs
        </h2>
      </div>

      <Card>
        {loading ? (
          <div className="flex justify-center py-10">
            <BallTriangle height={50} width={50} color="#4BB543" />
          </div>
        ) : urls.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            No URLs assigned yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    #
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    Course
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    Department
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    Batch Structure
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    Batch
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    Title
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    URL
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    Created Date
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold text-navy-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {urls.map((urlItem, index) => (
                  <tr
                    key={urlItem._id}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-sm text-navy-700">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-700">
                      {urlItem.course?.courseName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-700">
                      {urlItem.course?.department?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-700">
                      {urlItem.batchStructure?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-700">
                      {urlItem.batch?.batchNumber || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-700">
                      {urlItem.title}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <a
                        href={urlItem.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {urlItem.url.substring(0, 30)}...
                      </a>
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-700">
                      {urlItem.createdAt
                        ? formatDate(new Date(urlItem.createdAt))
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditClick(urlItem)}
                          className="rounded bg-blue-500 px-3 py-1 text-sm text-white hover:bg-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(urlItem._id)}
                          className="rounded bg-red-500 px-3 py-1 text-sm text-white hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmationModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onConfirm={handleUpdateSubmit}
        title="Edit Training Material URL"
        message={
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium">Title</label>
              <input
                type="text"
                name="title"
                value={editForm.title}
                onChange={handleEditInputChange}
                className="mt-1 w-full rounded border border-gray-300 p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">URL</label>
              <input
                type="url"
                name="url"
                value={editForm.url}
                onChange={handleEditInputChange}
                className="mt-1 w-full rounded border border-gray-300 p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Description</label>
              <textarea
                name="description"
                value={editForm.description}
                onChange={handleEditInputChange}
                className="mt-1 w-full rounded border border-gray-300 p-2"
                rows="2"
              />
            </div>
          </div>
        }
        confirmText="Update"
        cancelText="Cancel"
        icon="info"
        showActions={false}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message="Are you sure you want to delete this URL? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        icon="warning"
      />
    </div>
  );
};

export default Index;
